import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { usePrevious } from 'react-use';
import { MAX_RESOLUTION } from '../../../../../config/constant/unityConstants';
import { selectSettingsResolutionPreset } from '../../../../../helpers/curatorActions';
import { stripNonNumbers } from '../../../../../helpers/jsHelper';
import { errorToast } from '../../../../../helpers/toastHelper';
import { normalizeResSize } from '../../../../../helpers/unityHelper';
import {
  selectResolutionPresets,
  setSelectedResolutionPreset,
  updateResolutionPreset,
} from '../../../../../redux/slicers/admin/curatorSlicer';
import {
  setGlobalHeight,
  setGlobalSafeCameraFlag,
  setGlobalWidth,
} from '../../../../../redux/slicers/camera/cameraSettings';
import { useUnityContext } from '../../../../container/unityContainer';
import { undoRedo } from '../../UndoRedo/UndoRedo';

export const useResolutionHandlers = ({
  data,
  keyX = 'width',
  keyY = 'height',
  defaultX,
  defaultY,
  updateCallback,
  min,
  max,
  onBlur,
  innerRef,
  ratioParent,
  valueFormatter = parseFloat,
}) => {
  const selectedItem = data;
  const ratioRef = useRef(1);
  const lock = data?.lock || false;
  const prevLock = usePrevious(data?.lock || false);

  const changeCallback = (data, eventDetails) => {
    const formattedData = {
      [keyX]:
        typeof data[keyX] === 'string' && data[keyX].trim() === ''
          ? data[keyX]
          : valueFormatter(data[keyX]),
      [keyY]:
        typeof data[keyY] === 'string' && data[keyY].trim() === ''
          ? data[keyY]
          : valueFormatter(data[keyY]),
    };
    updateCallback(formattedData, eventDetails);
  };

  useEffect(() => {
    if (!prevLock && lock) {
      ratioRef.current = data[keyX] / data[keyY];
    } else {
      ratioRef.current = 1;
    }
  }, [lock]);


  if (innerRef) {
    innerRef.current = {
      refreshLockRatio: () => {
        if (lock) {
          ratioRef.current = data[keyX] / data[keyY];
        }
      }
    }
  }



  const handleResolutionChange = (e, details) => {
    let { name, value } = e.target;

    if (max && `${value}`.length > `${max}`.length) {
      return;
    }

    const data = {
      [keyX]: selectedItem[keyX],
      [keyY]: selectedItem[keyY],
    };

    if (value.trim() === '') {
      if (lock) {
        data[keyX] = value;
        data[keyY] = value;
      } else {
        data[name] = value;
      }
      changeCallback(data, { type: 'change', triggeredBy: details?.triggeredBy });
      return;
    }

    value = parseFloat(value, 10) || 0;

    if (lock) {
      if (name === keyX) {
        const height = parseFloat(value / (ratioParent ? ratioParent : ratioRef.current));
        data[keyX] = value;
        data[keyY] = height;
      } else {
        const width = parseFloat(value * (ratioParent ? ratioParent : ratioRef.current));
        data[keyY] = value;
        data[keyX] = width;
      }

      if (max) {
        if (data[keyY] > max) {
          const divider = data[keyY] / max;
          data[keyY] = max;
          data[keyX] = data[keyX] / divider; 
        } 
        else if (data[keyX] > max) {
          const divider = data[keyX] / max;
          data[keyX] = max;
          data[keyY] = data[keyY] / divider; 
        }
      }

      if (min) {
        if (data[keyY] < min) {
          data[keyY] = min;
        } else if (data[keyX] < min) {
          data[keyX] = min;
        }
      }
      
    } else {
      data[name] = value;
    }

    changeCallback(data, { type: 'change', triggeredBy: details?.triggeredBy });
  };

  const handleResolutionBlur = (e, details) => {
    let { name, value } = e.target;

    if (value.trim() === '' || parseFloat(value) === 0 || Number.isNaN(parseFloat(value)) || parseFloat(value) < min) {
      const data = {
        [keyX]: selectedItem[keyX] || defaultX,
        [keyY]: selectedItem[keyY] || defaultY,
      };

      if (data[keyX] < min) {
        data[keyX] = defaultX;
      }

      if (data[keyY] < min) {
        data[keyY] = defaultY;
      }

      if (lock) {
        if (ratioRef.current > 1) {
          data[keyX] = parseFloat(ratioRef.current * data[keyX]);
        } else {
          data[keyY] = parseFloat(data[keyX] / ratioRef.current);
        }
      }

      changeCallback(data, { type: 'blur', triggeredBy: details?.triggeredBy });
      return;
    }

    const data = {
      [keyX]: selectedItem[keyX],
      [keyY]: selectedItem[keyY],
    };

    changeCallback(data, { type: 'blur', triggeredBy: details?.triggeredBy });
  };

  const handleSimpleBlur = (e) => {
    const data = {
      [keyX]: selectedItem[keyX],
      [keyY]: selectedItem[keyY],
    };
    onBlur?.(data);
  };

  return {
    handleResolutionBlur,
    handleSimpleBlur,
    handleResolutionChange,
  };
};

export const useResolutionPresets = () => {
  const dispatch = useDispatch();
  const { cameraModule } = useUnityContext();
  const resolutionPresets = useSelector(selectResolutionPresets);
  const selectedItem = resolutionPresets?.find((rp) => rp.isSelected);
  const resolutionValuesRef = useRef({ width: selectedItem?.width, height: selectedItem?.height });

  useEffect(() => {
    resolutionValuesRef.current = {
      width: selectedItem?.width, 
      height: selectedItem?.height,
    }
  }, [selectedItem?.id])

  const updateResolution = (data, { type }) => {
    const width = data.width ? normalizeResSize(data.width) : data.width;
    const height = data.height ? normalizeResSize(data.height) : data.height;

    const newResolution = { width, height };

    dispatch(
      updateResolutionPreset({
        id: selectedItem.id,
        ...newResolution,
      })
    );

    dispatch(setGlobalWidth(width));
    dispatch(setGlobalHeight(height));
    dispatch(setGlobalSafeCameraFlag(true));

    cameraModule.changeSafeFrameResolution({ 
      cameraWidth: data.width,
      cameraHeight: data.height,
    })

    if (type === 'blur') {
      
      undoRedo.settings.updateResolution({
        id: selectedItem.id,
        nextValue: newResolution,
        prevValue: resolutionValuesRef.current,
      })

      resolutionValuesRef.current = newResolution; // store latest data to ref
    }
  };

  const { handleResolutionBlur, handleResolutionChange } = useResolutionHandlers({
    data: selectedItem,
    keyX: 'width',
    keyY: 'height',
    defaultX: 1,
    defaultY: 1,
    valueFormatter: parseInt,
    updateCallback: updateResolution,
  });

  const handleSelect = (item) => () => {
    if (!selectedItem.name.trim()) {
      errorToast('Resolution name is required', {
        toastId: 'RESOLUTION_NAME_REQUIRED' + selectedItem.id,
      });
      return;
    }

    selectSettingsResolutionPreset(item, true)

    cameraModule.changeSafeFrameResolution({ 
      cameraWidth: item.width,
      cameraHeight: item.height,
    })

    undoRedo.settings.selectResolutionPreset({
      selectedItem,
      nextSelectedItem: item,
    })
  };

  const toggleLock = () => {
    dispatch(
      updateResolutionPreset({
        id: selectedItem.id,
        lock: !selectedItem.lock,
      })
    );

    // undo/redo
    undoRedo.settings.toggleResolutionPresetLock({
      id: selectedItem.id,
      prevValue: selectedItem.lock,
      nextValue: !selectedItem.lock,
    });
  };

  return {
    resolutionPresets,
    selectedItem,
    toggleLock,
    handleBlur: handleResolutionBlur,
    handleChange: handleResolutionChange,
    handleSelect,
  };
};
